import React, { useMemo, useEffect, useState, useRef } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  of,
  switchMap,
  Subject,
  map,
  tap,
  catchError,
  combineLatest,
  takeUntil,
} from "rxjs";
import apiClient from "../../services/apiClient";
import { validate } from "../../state/helpers/validate";
import { getTypeOfQuery } from "../../common/helpers/helpers";
import PubmedEntry from "./PubmedEntry";
import { getGenome } from "../../state/selectors/general.selectors";
import VariantForGenome from "./VariantForGenome";
import helpers, {
  ADDITIONAL_INFO_OPTIONS,
  VARSOME_VARIANT_CLASSES,
} from "../../common/utils/utils";
import tailwindColors from "../../../../tailwind.colors";
import Btn from "../atoms/Btn";
import theme from "../../../../select.theme";

const selectStyle = {
  control: (base, state) => ({
    ...base,
    borderRadius: "0px",
    padding: "2px 1px",
    boxShadow: state.isFocused ? 0 : 0,
    borderColor: state.isFocused
      ? tailwindColors.primary
      : tailwindColors.blueGray[300],
    "&:hover": {
      borderColor: state.isFocused
        ? tailwindColors.primary
        : tailwindColors.blueGray[300],
    },
  }),
};
function VariantScreen({
  entry,
  onEntryUpdated,
  genomeId,
  infoOptions,
  varsomeClasses,
}) {
  const destroy$ = useRef(new Subject()).current;
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const hg19Ids = entry.groupedVariants.map((x) => x?.hg19?.variant_id);
  const hg38Ids = entry.groupedVariants.map((x) => x?.hg38?.variant_id);
  const allIds = [...hg19Ids, ...hg38Ids];
  const idSet = new Set(allIds);

  const [clArr, clMap] = useMemo(() => {
    const options = varsomeClasses.map((x) => [x.value, x.label]);
    const optionDict = options.reduce((a, x) => a.set(x[0], x[1]), new Map());
    return [varsomeClasses, optionDict];
  }, []);

  const [infArr, infMap] = useMemo(() => {
    const classes = infoOptions.map((x) => [x.value, x.label]);
    const options = classes.map((x) => ({ value: x[0], label: x[1] }));
    const optionDict = classes.reduce((a, x) => a.set(x[0], x[1]), new Map());
    return [options, optionDict];
  }, []);

  function addVariants(newVars) {
    const newVariants = newVars.map((x) => ({
      ...x,
      checked: true,
      pathogenicity: null,
      additional_info: [],
      id: x.hg19.variant_id || x.hg38.variant_id,
    }));

    entry.groupedVariants = entry.groupedVariants.concat(newVariants);
    onEntryUpdated(entry.clone());
  }

  function updateVariants(x) {
    entry.groupedVariants = x;
    onEntryUpdated(entry.clone());
  }

  function errHandler(e) {
    console.error(e);
    setError("Error fetching possible variants for this query");
    return of(null);
  }

  function groupGenomes(data) {
    const hg19Vars = data?.hg19 || [];
    const hg38Vars = data?.hg38 || [];
    const maxLen = Math.max(hg19Vars.length, hg38Vars.length);

    return Array(maxLen)
      .fill(null)
      .map((_, i) => ({ hg19: hg19Vars[i], hg38: hg38Vars[i] }));
  }

  function filterVariants(variants) {
    const filtered = variants
      .filter((y) => !idSet.has(y?.hg19?.variant_id))
      .filter((y) => !idSet.has(y?.hg38?.variant_id));

    if (!filtered.length) {
      setError("Variant already exists in the list");
    }

    return filtered;
  }

  function search() {
    const formattedQuery = helpers.formatUserQuery(query);
    const genome = getTypeOfQuery(query) === "chr" ? genomeId : null;

    if (validate(formattedQuery, true).error) {
      setError("Invalid query");
      setIsLoading(false);
      return;
    }

    of(null)
      .pipe(
        tap(() => setError("")),
        tap(() => setIsLoading(true)),
        switchMap(() => apiClient.getVariants(formattedQuery, genome)),
        map((x) => groupGenomes(x)),
        map(filterVariants),
        tap((x) => addVariants(x)),
        catchError(errHandler),
        tap(() => setIsLoading(false)),
        takeUntil(destroy$)
      )
      .subscribe();
  }

  useEffect(() => {
    const { variants, groupedVariants } = entry;

    function extractPatho(value) {
      return { value, label: clMap.get(value) };
    }

    function extractInfo(value) {
      return { value, label: infMap.get(value) };
    }

    function getInfo(variant) {
      return of(null).pipe(
        switchMap(() => apiClient.getVariants(variant.variant_id)),
        map((x) => groupGenomes(x)),
        map((vars) =>
          vars.map((x) => ({
            ...x,
            checked: true,
            id: x.hg19.variant_id || x.hg38.variant_id,
            pathogenicity: extractPatho(variant.pathogenicity),
            additional_info: (variant?.additional_info || []).map(extractInfo),
          }))
        ),
        catchError((err) => {
          console.error(err);
          return of(null);
        }),
        takeUntil(destroy$)
      );
    }

    if (variants.length > 0 && groupedVariants.length === 0) {
      const reqs = variants.map(getInfo);

      combineLatest(reqs).subscribe((res) => {
        const allVars = [].concat(...res);
        const varMap = allVars.reduce((a, x) => a.set(x.id, x), new Map());
        const vars = Array.from(varMap.values());
        entry.groupedVariants = vars;
        onEntryUpdated(entry.clone());
      });
    }
  }, []);

  useEffect(() => {
    return () => destroy$.next();
  }, []);

  return (
    <>
      <div className="tw-font-bold text-larger-3">2. Variants </div>

      {entry?.groupedVariants?.length > 0 ? (
        <div className="tw-mb-1">Variants:</div>
      ) : null}
      {entry.groupedVariants.map((variant) => (
        <div
          className="tw-mb-2 tw-p-1 tw-relative tw-border tw-border-blueGray-300 tw-ml-2"
          key={variant.id}
        >
          <input
            className="tw-absolute tw-top-0 tw-left-0 tw-transform tw--translate-x-1/2 tw--translate-y-1/2"
            type="checkbox"
            checked={variant.checked}
            onChange={(e) => {
              variant.checked = e.target.checked || false;
              updateVariants([...entry.groupedVariants]);
            }}
          />
          <div className="tw-flex tw-gap-6 tw-mx-4 tw-py-2">
            <VariantForGenome variant={variant} genomeKey="hg19" />
            <VariantForGenome variant={variant} genomeKey="hg38" />
            <div className="tw-flex-grow" />
          </div>
          <div className="tw-flex tw-gap-5">
            <Select
              className="tw-flex-grow tw-flex-1 tw-flex-shrink-0"
              styles={selectStyle}
              theme={theme}
              onChange={(val) => {
                variant.pathogenicity = val;
                updateVariants([...entry.groupedVariants]);
              }}
              options={clArr}
              value={variant.pathogenicity}
              isMulti={false}
              isClearable={false}
              menuPosition="fixed"
            />

            <Select
              className="tw-flex-grow tw-flex-1 tw-flex-shrink-0"
              styles={selectStyle}
              theme={theme}
              isMulti={true}
              value={variant.additional_info}
              onChange={(x) => {
                variant.additional_info = x;
                updateVariants([...entry.groupedVariants]);
              }}
              options={infArr}
              placeholder="Additional variant information..."
              menuPosition="fixed"
            />
          </div>
        </div>
      ))}

      <p className="tw-m-0 tw-pb-0.5">
        You may add more variants by searching below using any query format
        accepted by VarSome.
      </p>
      <div className="tw-flex tw-max-w-screen-sm">
        <input
          type="text"
          autoComplete="off"
          className="focus:tw-border-primary tw-px-1 tw-py-1 tw-border tw-border-blueGray-300 tw-outline-none tw-flex-grow"
          placeholder="Add variant(s)..."
          value={query}
          id="var-input"
          onChange={(e) => {
            setError(null);
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => e.key === "Enter" && search(query)}
        />
        <Btn
          onSubmit={() => search(query)}
          className="btn tw-w-32 btn-success tw-border-2"
          disabled={isLoading}
          id="var-submit"
        >
          Add variant(s)
        </Btn>
      </div>
      {error ? (
        <div className="tw-bg-red-300 tw-text-center tw-font-bold tw-text-white tw-py-6">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="tw-text-center tw-py-6">Loading...</div>
      ) : null}
    </>
  );
}

VariantScreen.propTypes = {
  entry: PropTypes.instanceOf(PubmedEntry).isRequired,
  onEntryUpdated: PropTypes.func.isRequired,
  infoOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  varsomeClasses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  genomeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

VariantScreen.defaultProps = {
  genomeId: "1019",
};

const mapStateToProps = (state) => {
  return {
    varsomeClasses: VARSOME_VARIANT_CLASSES,
    infoOptions: ADDITIONAL_INFO_OPTIONS,
    genomeId: getGenome(state)?.id,
  };
};

export default connect(mapStateToProps)(VariantScreen);
